
import Vue from 'vue'
import VueViewer from 'v-viewer'
import 'viewerjs/dist/viewer.css'

const VIEWER_Z_INDEX = 1e16

Vue.use(VueViewer, {
  defaultOptions: {
    zIndex: VIEWER_Z_INDEX,
  },
})

export default {
  name: 'ViewerImages',
  components: {
    ModalVideo: () => import('@/components/ModalVideo.vue'),
    ProductVideoPreview: () => import('@/components/ProductVideoPreview.vue'),
    ProductVideoShelf: () => import('@/components/ProductVideoShelf.vue'),
    CustomerMediaTeaser: () => import('@/components/CustomerMediaTeaser.vue'),
    CustomerMediaRow: () => import('@/components/CustomerMediaRow.vue'),
  },
  props: {
    sources: {
      type: Object,
      default: () => ({ images: [], movies: [], name: '' }),
    },
    dimensions: {
      type: Object,
      default: () => ({ width: 520, height: 520 }),
    },
    contain: {
      type: Boolean,
      default: false,
    },
    customerMedia: {
      type: Object,
      default: null,
    },
  },

  data() {
    let initialMainImg = null
    let initialVideo = null

    const moviesWithImage = this.sources?.movies?.filter(m => m.image) || []

    if (this.sources?.images?.length) {
      initialMainImg = this.sources.images[0]
    } else if (moviesWithImage.length) {
      initialMainImg = moviesWithImage[0]
    }
    if (moviesWithImage.length) {
      initialVideo = moviesWithImage[0]
    }

    return {
      mainImg: initialMainImg,
      videoPreview: initialVideo,
      modalId: `modalVideo-${Math.random().toString(36).substring(2, 15)}`,
      imagePreviewLoaded: false,
      hoverTimeout: null,
      loadedSlides: new Set(),
      touchStartX: 0,
      touchStartY: 0,
      touchDirection: null,
    }
  },

  computed: {
    display() {
      if (this.videos.length > 1) return 'C'
      return this.$device.isMobile ? 'A' : 'B'
    },
    videosHaveOwnEntry() {
      return (
        this.videos.length > 0 &&
        this.sources.images.length > 0 &&
        ['A', 'C'].includes(this.display)
      )
    },
    getAllSourcesForPreview() {
      if (this.videosHaveOwnEntry) return [...this.sources.images]
      return [...this.sources.images, ...this.videos]
    },
    thumbSources() {
      if (this.videosHaveOwnEntry) return [...this.sources.images]
      return [...this.sources.images, ...this.videos]
    },
    videos() {
      return (this.sources.movies || []).filter(m => m.image && m.video)
    },
    firstMedia() {
      return this.getAllSourcesForPreview[0] || null
    },
    restOfSources() {
      return this.getAllSourcesForPreview.slice(1)
    },
    firstImageSrc() {
      if (!this.firstMedia) return ''
      return this.$cropUrl(
        this.firstMedia?.image ?? this.firstMedia,
        400,
        400,
        this.firstMedia?.image?.extension ?? this.firstMedia?.extension,
        86
      )
    },
    firstImageSrcset() {
      if (!this.firstMedia) return ''
      return `${this.$cropUrl(
        this.firstMedia?.image ?? this.firstMedia,
        800,
        800,
        this.firstMedia?.image?.extension ?? this.firstMedia?.extension,
        86
      )} 2x`
    },
    hasMovies() {
      return this.sources.movies && this.sources.movies.length > 0
    },
    hasMedias() {
      return this.getAllSourcesForPreview.length > 0
    },
    previewImgClass() {
      return this.contain
        ? 'tw-object-contain tw-max-h-full tw-max-w-full tw-w-auto tw-h-auto'
        : 'w-100 w-down-sm-100'
    },
  },

  beforeDestroy() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
    }
  },
  methods: {
    onTouchStart(e) {
      const touch = e.touches[0]
      this.touchStartX = touch.pageX
      this.touchStartY = touch.pageY
      this.touchDirection = null
    },
    onTouchMove(e) {
      if (!this.touchDirection) {
        const dx = Math.abs(e.touches[0].pageX - this.touchStartX)
        const dy = Math.abs(e.touches[0].pageY - this.touchStartY)
        if (dx + dy < 10) return
        this.touchDirection = dx > dy ? 'horizontal' : 'vertical'
      }
      if (this.touchDirection === 'horizontal' && e.cancelable) {
        e.preventDefault()
      }
    },
    onTouchEnd() {
      this.touchDirection = null
    },
    getMobileImageSrcset(media) {
      const upload = media?.image ?? media
      const ext = media?.image?.extension ?? media?.extension
      const height = size => (this.contain ? '_' : size)
      const img320 = this.$cropUrl(upload, 320, height(320), ext, 86)
      const img400 = this.$cropUrl(upload, 400, height(400), ext, 86)
      const img800 = this.$cropUrl(upload, 800, height(800), ext, 86)
      return `${img320} 320w, ${img400} 400w, ${img800} 800w`
    },
    mediaPreviewSrc(media, width, height) {
      return this.$cropUrl(
        media?.image ?? media,
        width,
        this.contain ? '_' : height,
        media?.image?.extension ?? media?.extension,
        86
      )
    },
    mediaFullSrc(media) {
      return this.$cropUrl(
        media?.image ?? media,
        1200,
        this.contain ? '_' : 1200,
        media?.image?.extension ?? media?.extension,
        90
      )
    },
    goToFirstVideo() {
      const index = this.getAllSourcesForPreview.findIndex(m => m.video)
      if (index < 0 || !this.$refs.carousel) return
      this.loadedSlides = new Set([...this.loadedSlides, index])
      this.$refs.carousel.goto(index)
    },
    onSlideChange({ index }) {
      this.loadedSlides = new Set([...this.loadedSlides, index])
    },
    openViewer(index) {
      if (!process.client) return

      const images = this.getAllSourcesForPreview.map(media => ({
        src: this.mediaFullSrc(media),
        'data-source': this.$cropUrl(
          media?.image ?? media,
          80,
          80,
          media?.image?.extension ?? media?.extension,
          60
        ),
      }))

      this.$viewerApi({
        options: {
          initialViewIndex: index,
          movable: true,
          toolbar: true,
          navbar: true,
          zoomable: true,
          zoomOnTouch: true,
          zoomOnWheel: true,
          url: 'src',
          zIndex: VIEWER_Z_INDEX,
        },
        images,
      })
    },
    showImage(media) {
      if (!process.client) return
      const list = this.thumbSources
      const index = list.indexOf(media)
      const key = media?.id ?? media?.video?.id ?? index
      document.getElementsByClassName(`image-${key}`)[0]?.click()
    },
    showVideo(video, poster = null) {
      if (process.client) {
        this.$store.dispatch('setVideo', { ...video, poster })
        this.$bvModal.show(this.modalId)
      }
    },
    selectImage(media) {
      if (this.mainImg === media) return

      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
      }
      this.hoverTimeout = setTimeout(() => {
        this.mainImg = media
      }, 50)
    },
  },
}
